<template>
  <div
    v-if="showSecretKey"
    class="eu-mt_4"
  >
    <alert-limit-key
      v-if="showLimitedAlert"
      :limit="MAX_LIMIT_KEYS_PER_ACCOUNT"
    />

    <emd-card
      noBorder
      class="_footer_spaced_sm"
    >
      <emd-card-header>
        <div class="eu-display_flex eu-column">
          <emd-text size="section"> {{ $t('api-keys') }} </emd-text>
          <doc-key />
        </div>
        <template #actions>
          <emd-button
            v-if="showButtonCreate"
            color="success"
            :text="$t('button-create')"
            @click="isModalCreate = !isModalCreate"
          />
        </template>
      </emd-card-header>

      <emd-card-content customClass="emd-table-responsive eu-no-padding">
        <div
          v-if="isLoadingSecretKeysList"
          class="eu-pa_6 eu-align_center"
        >
          <emd-loader type="_invert" />
        </div>
        <Table
          v-else
          :fields="tableFields"
          :items="items"
          @delete="handleOpenModalDelete"
        />
        <empty-list
          v-if="showEmptyList"
          entity="secretKeys"
          svg="no-results"
        />
        <pagination
          v-if="!isLoadingSecretKeysList"
          type="list"
          :action="GET_SECRET_KEY_ACTION"
          :pagination="pagination"
          :totalShowing="totalItems"
        />
      </emd-card-content>
    </emd-card>

    <modal-create-key
      v-if="isModalCreate"
      @success="handleSuccessCreate"
      @handleClose="() => (isModalCreate = false)"
    />
    <modal-copy-key-content
      v-if="isModalCopy"
      :secretKey="secretKey.key"
      @handleClose="() => (isModalCopy = false)"
    />
    <modal-delete-key-content
      v-if="isModalDelete"
      :secretKey="secretKey"
      @success="handleGetItems"
      @handleClose="() => (isModalDelete = false)"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  EmdText,
  EmdCard,
  EmdCardHeader,
  EmdCardContent,
  EmdButton,
  EmdLoader
} from 'emerald-vue/src/components'

import {
  isSecretKeyDeleteEnabled,
  isSecretKeyCreateEnabled,
  isSecretKeyViewEnabled
} from '@/actions-permission/keys.permissions'

import { structuredClone } from '@/utils/structured-clone.utils'

import { getFields } from './enums/secret-keys-fields.enum'

export default {
  name: 'SecretKeys',

  components: {
    EmdText,
    EmdCard,
    EmdCardHeader,
    EmdCardContent,
    EmdButton,
    EmdLoader,
    Table: () => import('@/components/project/molecules/Table.vue'),
    EmptyList: () => import('@/components/project/globals/EmptyList.vue'),
    Pagination: () => import('@/components/project/globals/Pagination.vue'),
    DocKey: () => import('./DocKey.vue'),
    ModalCreateKey: () => import('./modals/ModalCreateKey/index.vue'),
    ModalCopyKeyContent: () => import('./modals/ModalCopyKeyContent.vue'),
    ModalDeleteKeyContent: () => import('./modals/ModalDeleteKeyContent.vue'),
    AlertLimitKey: () => import('./AlertLimitKey.vue')
  },

  data () {
    return {
      isModalCopy: false,
      isModalCreate: false,
      isModalDelete: false,
      MAX_LIMIT_KEYS_PER_ACCOUNT: 10,
      GET_SECRET_KEY_ACTION: 'GET_SECRET_KEY',
      secretKey: {
        masked_key: '',
        key: undefined,
        id: undefined
      }
    }
  },

  computed: {
    ...mapGetters([
      'hasUserTwoFactorAuthentication',
      'permissionIsEnabled',
      'isEmployee',
      'secretKeyList',
      'isLoadingSecretKeysList'
    ]),

    tableFields () {
      return getFields(this.isEmployee, this.showButtonDelete) //
        .map(item => ({
          ...item,
          label: this.$t(item.label)
        }))
    },

    pagination () {
      return this.secretKeyList?.pagination
    },

    items () {
      return this.handleItems()
    },

    totalItems () {
      return this.items?.length ?? 0
    },

    showLimitedAlert () {
      return this.totalItems >= this.MAX_LIMIT_KEYS_PER_ACCOUNT
    },

    showEmptyList () {
      return !this.isLoadingSecretKeysList && this.totalItems === 0
    },

    showButtonCreate () {
      return isSecretKeyCreateEnabled(
        this.permissionIsEnabled,
        this.hasUserTwoFactorAuthentication,
        !this.showLimitedAlert
      )
    },

    showButtonDelete () {
      return isSecretKeyDeleteEnabled(
        this.permissionIsEnabled,
        this.hasUserTwoFactorAuthentication
      )
    },

    showSecretKey () {
      return isSecretKeyViewEnabled(this.permissionIsEnabled)
    }
  },

  methods: {
    ...mapActions(['GET_SECRET_KEY']),

    handleOpenModalDelete (secretKey) {
      this.secretKey = secretKey
      this.isModalDelete = true
    },

    handleSuccessCreate (secretKey) {
      this.secretKey = secretKey
      this.isModalCopy = true
      this.handleGetItems()
    },

    handleGetItems () {
      this.GET_SECRET_KEY()
    },

    handleItems () {
      const TYPE_KEY_ENUM = {
        TOTAL: 'Total',
        CUSTOM: 'Custom'
      }

      const items = structuredClone(this.secretKeyList?.items) ?? []

      return items.map(secretKeyRowData => {
        if (secretKeyRowData.scope?.length <= 0) {
          secretKeyRowData.scope = ''
          return secretKeyRowData
        }

        const scopeList = secretKeyRowData.scope.map(scope => scope.value)
        const hasTotal = scopeList.some(value =>
          value.includes(TYPE_KEY_ENUM.TOTAL)
        )

        secretKeyRowData.scope = hasTotal
          ? this.$t(`scope-total`)
          : this.$t(`scope-custom`)

        secretKeyRowData.tooltip = scopeList

        return secretKeyRowData
      })
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "api-keys": "Chaves da API",
      "button-create": "Criar chave",
      "list": {
        "id": "ID",
        "key": "Chave",
        "name": "Nome",
        "createdAt": "Criado Em",
        "updateAt": "Última atualização",
        "access": "Acesso"
      },
      "scope-total": "Total",
      "scope-custom": "Personalizado"
    },
    "en-US": {
      "api-keys": "API Keys",
      "button-create": "Create key",
      "list": {
        "id": "ID",
        "key": "Key",
        "name": "Name",
        "createdAt": "Created at",
        "updateAt": "Last updated",
        "access": "Access"
      },
      "scope-total": "Total",
      "scope-custom": "Custom"
    }
  }
</i18n>
